import React, { ReactNode, useMemo } from "react";
import { cx, useTranslations } from "@jugl-web/utils";
import { useWorkspaceFeatureCardData } from "@web-src/modules/workspace/hooks/useWorkspaceFeatureCardData/useWorkspaceFeatureCardData";
import { SubscriptionPlanModuleId } from "@jugl-web/rest-api";
import { ReactComponent as CheckIcon } from "./assets/check.svg";
import { ReactComponent as CrossIcon } from "./assets/cross.svg";

import { ReactComponent as ChatsIcon } from "./assets/chats.svg";
import { ReactComponent as PeopleIcon } from "./assets/people.svg";
import { ReactComponent as DriveIcon } from "./assets/drive.svg";
import { ReactComponent as ClientsIcon } from "./assets/clients.svg";
import { ReactComponent as TasksIcon } from "./assets/tasks.svg";
import { ReactComponent as FormsIcon } from "./assets/forms.svg";
import { ReactComponent as CatalogIcon } from "./assets/catalog.svg";
import { ReactComponent as WhatsAppCrmIcon } from "./assets/whatsapp.svg";
import { ReactComponent as TravelLogIcon } from "./assets/travel-log.svg";
import { ReactComponent as DashboardIcon } from "./assets/dashboard.svg";
import { ReactComponent as IntegrationsIcon } from "./assets/integrations.svg";
import { ReactComponent as InvoicesIcon } from "./assets/invoices.svg";
import { ReactComponent as AIAgentsIcon } from "./assets/ai-agents.svg";
import { ReactComponent as AutomationIcon } from "./assets/automation.svg";

export const ModuleChangeList: React.FC<{
  items: string[];
  type: "added" | "removed";
}> = ({ items, type }) => {
  const modulesData = useWorkspaceFeatureCardData();
  const { t } = useTranslations();
  const isAdded = type === "added";

  const modules = useMemo(
    () =>
      items
        .map((item) => modulesData[item as SubscriptionPlanModuleId])
        .filter((item) => item !== undefined),
    [items, modulesData]
  );

  const moduleIcons: Record<SubscriptionPlanModuleId, ReactNode> = {
    [SubscriptionPlanModuleId.chat]: <ChatsIcon />,
    [SubscriptionPlanModuleId.calls]: <ChatsIcon />,
    [SubscriptionPlanModuleId.people]: <PeopleIcon />,
    [SubscriptionPlanModuleId.timeLog]: <TravelLogIcon />,
    [SubscriptionPlanModuleId.drive]: <DriveIcon />,
    [SubscriptionPlanModuleId.clients]: <ClientsIcon />,
    [SubscriptionPlanModuleId.taskSharing]: <TasksIcon />,
    [SubscriptionPlanModuleId.workato]: <IntegrationsIcon />,
    [SubscriptionPlanModuleId.task]: <TasksIcon />,
    [SubscriptionPlanModuleId.orderForm]: <FormsIcon />,
    [SubscriptionPlanModuleId.inventory]: <CatalogIcon />,
    [SubscriptionPlanModuleId.whatsappCrm]: <WhatsAppCrmIcon />,
    [SubscriptionPlanModuleId.travelLog]: <TravelLogIcon />,
    [SubscriptionPlanModuleId.dashboard]: <DashboardIcon />,
    [SubscriptionPlanModuleId.integration]: <IntegrationsIcon />,
    [SubscriptionPlanModuleId.invoices]: <InvoicesIcon />,
    [SubscriptionPlanModuleId.aiAgents]: <AIAgentsIcon />,
    [SubscriptionPlanModuleId.automation]: <AutomationIcon />,
  };

  if (!modules.length) return null;
  return (
    <div className="mt-8 flex w-full flex-col items-start gap-2">
      <div className="flex items-center gap-2">
        <div
          className={cx(
            "flex h-[17px] w-[17px] items-center justify-start rounded-full border-[1.5px] border-solid",
            isAdded ? "border-secondary" : "border-tertiary"
          )}
        >
          {isAdded ? <CheckIcon /> : <CrossIcon />}
        </div>
        <span className="font-secondary text-xs leading-[21px] font-normal text-dark-800 italic">
          {isAdded
            ? t({
                id: "workspace-features-page.whats-been-added-2",
                defaultMessage: "Modules that's been added",
              })
            : t({
                id: "workspace-features-page.whats-been-removed-2",
                defaultMessage: "Modules that's been removed ",
              })}
        </span>
      </div>
      <div className="jugl__custom-scrollbar-horizontal flex w-full items-center justify-start gap-2 overflow-x-auto">
        {items.map((module, idx) => (
          <div
            key={+idx}
            className="flex h-[33px] w-max shrink-0 items-center justify-center gap-1 rounded-md bg-grey-100 px-2 font-secondary text-sm leading-[21px] text-dark-800"
          >
            {moduleIcons[module as SubscriptionPlanModuleId]}
            {modulesData[module as SubscriptionPlanModuleId]?.title}
          </div>
        ))}
      </div>
    </div>
  );
};
